import React, { useState, useEffect } from 'react'
import { Repeat } from 'lucide-react'
import {
  Frequency,
  Weekday,
  type RRule,
  serializeRRule,
  describeRRule,
  parseRRule,
} from '@/lib/calendar/rrule-parser'
import { format, addDays, addWeeks, addMonths } from 'date-fns'

export interface RecurrenceConfigProps {
  value?: string | null
  onChange: (rruleString: string | null) => void
  startDate?: Date
}

type EndCondition = 'never' | 'count' | 'until'

/**
 * RecurrenceConfig Component
 *
 * Provides UI for configuring recurring event patterns using RFC 5545 RRULE format.
 * Supports DAILY, WEEKLY, and MONTHLY frequencies with interval, count, and until options.
 */
export default function RecurrenceConfig({
  value,
  onChange,
  startDate = new Date(),
}: RecurrenceConfigProps) {
  const [isRecurring, setIsRecurring] = useState<boolean>(false)
  const [frequency, setFrequency] = useState<Frequency>(Frequency.WEEKLY)
  const [interval, setInterval] = useState<number>(1)
  const [endCondition, setEndCondition] = useState<EndCondition>('never')
  const [count, setCount] = useState<number>(10)
  const [untilDate, setUntilDate] = useState<string>('')
  const [selectedWeekdays, setSelectedWeekdays] = useState<Weekday[]>([Weekday.MO])

  // Initialize from existing RRULE value
  useEffect(() => {
    if (value) {
      try {
        const parsed = parseRRule(value)
        setIsRecurring(true)
        setFrequency(parsed.freq)
        setInterval(parsed.interval)

        if (parsed.count) {
          setEndCondition('count')
          setCount(parsed.count)
        } else if (parsed.until) {
          setEndCondition('until')
          setUntilDate(format(parsed.until, 'yyyy-MM-dd'))
        } else {
          setEndCondition('never')
        }

        if (parsed.byDay && parsed.byDay.length > 0) {
          setSelectedWeekdays(parsed.byDay)
        }
      } catch (error) {
        console.error('Failed to parse RRULE:', error)
        setIsRecurring(false)
      }
    } else {
      setIsRecurring(false)
    }
  }, [value])

  // Generate RRULE string whenever configuration changes
  useEffect(() => {
    if (!isRecurring) {
      onChange(null)
      return
    }

    try {
      const rule: RRule = {
        freq: frequency,
        interval,
      }

      // Add end condition
      if (endCondition === 'count' && count > 0) {
        rule.count = count
      } else if (endCondition === 'until' && untilDate) {
        const until = new Date(untilDate)
        until.setHours(23, 59, 59, 999) // End of day
        rule.until = until
      }

      // Add BYDAY for weekly frequency
      if (frequency === Frequency.WEEKLY && selectedWeekdays.length > 0) {
        rule.byDay = selectedWeekdays
      }

      const rruleString = serializeRRule(rule)
      onChange(rruleString)
    } catch (error) {
      console.error('Failed to generate RRULE:', error)
      onChange(null)
    }
  }, [isRecurring, frequency, interval, endCondition, count, untilDate, selectedWeekdays, onChange])

  const toggleWeekday = (day: Weekday) => {
    setSelectedWeekdays((prev) => {
      if (prev.includes(day)) {
        // Don't allow removing the last weekday
        if (prev.length === 1) return prev
        return prev.filter((d) => d !== day)
      }
      return [...prev, day].sort((a, b) => {
        const order = [
          Weekday.SU,
          Weekday.MO,
          Weekday.TU,
          Weekday.WE,
          Weekday.TH,
          Weekday.FR,
          Weekday.SA,
        ]
        return order.indexOf(a) - order.indexOf(b)
      })
    })
  }

  const getOccurrencePreview = (): string[] => {
    if (!isRecurring) return []

    try {
      const rule: RRule = {
        freq: frequency,
        interval,
      }

      if (endCondition === 'count' && count > 0) {
        rule.count = Math.min(count, 5) // Preview first 5
      } else {
        rule.count = 5 // Just preview 5 occurrences
      }

      if (frequency === Frequency.WEEKLY && selectedWeekdays.length > 0) {
        rule.byDay = selectedWeekdays
      }

      // Generate preview occurrences manually (simplified)
      const occurrences: Date[] = []
      let currentDate = new Date(startDate)
      let generated = 0

      while (generated < (rule.count || 5)) {
        if (frequency === Frequency.DAILY) {
          occurrences.push(new Date(currentDate))
          currentDate = addDays(currentDate, interval)
          generated++
        } else if (frequency === Frequency.WEEKLY) {
          if (rule.byDay && rule.byDay.length > 0) {
            // Find next occurrence on selected weekdays
            const currentDay = currentDate.getDay()
            let foundNext = false

            for (let i = 0; i < 7 * interval && generated < (rule.count || 5); i++) {
              const testDate = addDays(currentDate, i)
              const testDay = testDate.getDay()
              const weekdayMap = [
                Weekday.SU,
                Weekday.MO,
                Weekday.TU,
                Weekday.WE,
                Weekday.TH,
                Weekday.FR,
                Weekday.SA,
              ]

              if (rule.byDay.includes(weekdayMap[testDay])) {
                occurrences.push(new Date(testDate))
                currentDate = addDays(testDate, 1)
                generated++
                foundNext = true
              }
            }

            if (!foundNext) break
          } else {
            occurrences.push(new Date(currentDate))
            currentDate = addWeeks(currentDate, interval)
            generated++
          }
        } else if (frequency === Frequency.MONTHLY) {
          occurrences.push(new Date(currentDate))
          currentDate = addMonths(currentDate, interval)
          generated++
        }
      }

      return occurrences.map((date) => format(date, 'dd/MM/yyyy'))
    } catch (error) {
      return []
    }
  }

  const occurrencesPreview = getOccurrencePreview()

  return (
    <div className="space-y-4">
      {/* Recurrence Toggle */}
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="recurrence-toggle"
          checked={isRecurring}
          onChange={(e) => setIsRecurring(e.target.checked)}
          className="w-4 h-4 rounded border-brand-gray/30 bg-brand-dark-lighter text-brand-primary focus:ring-2 focus:ring-brand-primary focus:ring-offset-0"
        />
        <label htmlFor="recurrence-toggle" className="flex items-center gap-2 font-medium text-white cursor-pointer">
          <Repeat size={16} className="text-brand-primary" />
          Repetir evento
        </label>
      </div>

      {/* Recurrence Configuration */}
      {isRecurring && (
        <div className="space-y-4 pl-7">
          {/* Frequency Selector */}
          <div>
            <label className="block text-sm font-medium text-brand-gray mb-2">
              Frequência
            </label>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as Frequency)}
              className="w-full px-3 py-2 rounded-monumenta bg-brand-dark-lighter border border-brand-gray/20 text-white focus:border-brand-primary focus:outline-none transition-all text-sm"
            >
              <option value={Frequency.DAILY}>Diariamente</option>
              <option value={Frequency.WEEKLY}>Semanalmente</option>
              <option value={Frequency.MONTHLY}>Mensalmente</option>
            </select>
          </div>

          {/* Interval Input */}
          <div>
            <label className="block text-sm font-medium text-brand-gray mb-2">
              Repetir a cada
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={99}
                value={interval}
                onChange={(e) => setInterval(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-20 px-3 py-2 rounded-monumenta bg-brand-dark-lighter border border-brand-gray/20 text-white focus:border-brand-primary focus:outline-none transition-all text-sm"
              />
              <span className="text-sm text-brand-gray">
                {frequency === Frequency.DAILY && (interval === 1 ? 'dia' : 'dias')}
                {frequency === Frequency.WEEKLY && (interval === 1 ? 'semana' : 'semanas')}
                {frequency === Frequency.MONTHLY && (interval === 1 ? 'mês' : 'meses')}
              </span>
            </div>
          </div>

          {/* Weekday Selector (for WEEKLY frequency) */}
          {frequency === Frequency.WEEKLY && (
            <div>
              <label className="block text-sm font-medium text-brand-gray mb-2">
                Dias da semana
              </label>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'Dom', value: Weekday.SU },
                  { label: 'Seg', value: Weekday.MO },
                  { label: 'Ter', value: Weekday.TU },
                  { label: 'Qua', value: Weekday.WE },
                  { label: 'Qui', value: Weekday.TH },
                  { label: 'Sex', value: Weekday.FR },
                  { label: 'Sáb', value: Weekday.SA },
                ].map((day) => (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => toggleWeekday(day.value)}
                    className={`px-3 py-2 rounded-monumenta text-sm font-medium transition-all ${
                      selectedWeekdays.includes(day.value)
                        ? 'bg-brand-primary text-white'
                        : 'bg-brand-dark-lighter text-brand-gray border border-brand-gray/20 hover:border-brand-gray/40'
                    }`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* End Condition */}
          <div>
            <label className="block text-sm font-medium text-brand-gray mb-2">
              Termina
            </label>
            <div className="space-y-2">
              {/* Never */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="end-condition"
                  checked={endCondition === 'never'}
                  onChange={() => setEndCondition('never')}
                  className="w-4 h-4 text-brand-primary focus:ring-brand-primary focus:ring-offset-0"
                />
                <span className="text-sm text-white">Nunca</span>
              </label>

              {/* After N occurrences */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="end-condition"
                  checked={endCondition === 'count'}
                  onChange={() => setEndCondition('count')}
                  className="w-4 h-4 text-brand-primary focus:ring-brand-primary focus:ring-offset-0"
                />
                <span className="text-sm text-white">Após</span>
                <input
                  type="number"
                  min={1}
                  max={999}
                  value={count}
                  onChange={(e) => {
                    setCount(Math.max(1, parseInt(e.target.value) || 1))
                    setEndCondition('count')
                  }}
                  disabled={endCondition !== 'count'}
                  className="w-16 px-2 py-1 rounded text-sm bg-brand-dark-lighter border border-brand-gray/20 text-white focus:border-brand-primary focus:outline-none disabled:opacity-50"
                />
                <span className="text-sm text-white">ocorrências</span>
              </label>

              {/* On specific date */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="end-condition"
                  checked={endCondition === 'until'}
                  onChange={() => setEndCondition('until')}
                  className="w-4 h-4 text-brand-primary focus:ring-brand-primary focus:ring-offset-0"
                />
                <span className="text-sm text-white">Em</span>
                <input
                  type="date"
                  value={untilDate}
                  onChange={(e) => {
                    setUntilDate(e.target.value)
                    setEndCondition('until')
                  }}
                  disabled={endCondition !== 'until'}
                  className="px-2 py-1 rounded text-sm bg-brand-dark-lighter border border-brand-gray/20 text-white focus:border-brand-primary focus:outline-none disabled:opacity-50"
                />
              </label>
            </div>
          </div>

          {/* Recurrence Summary */}
          {value && (
            <div className="p-3 rounded-monumenta bg-brand-primary/10 border border-brand-primary/20">
              <p className="text-sm text-brand-primary font-medium">
                📅 {describeRRule(value)}
              </p>
            </div>
          )}

          {/* Occurrence Preview */}
          {occurrencesPreview.length > 0 && (
            <div className="p-3 rounded-monumenta bg-brand-dark border border-brand-gray/20">
              <p className="text-xs font-medium text-brand-gray mb-2">
                Primeiras ocorrências:
              </p>
              <div className="flex flex-wrap gap-2">
                {occurrencesPreview.map((date, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 rounded text-xs bg-brand-dark-lighter text-white border border-brand-gray/20"
                  >
                    {date}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
